<template>
    <header
        class="flex justify-between lg:z-50 lg:h-20 py-3 px-4 lg:py-5 lg:px-6 gap-4 lg:gap-6 items-center
            border-b border-outline-variant relative lg:sticky top-0 inset-x-0 bg-background z-10 h-[64px]">
        <div class="bg-primary-30 ms-[-16px] w-[64px] aspect-square flex items-center justify-center shrink-0 lg:hidden">
            <RouterLink to="/"><img :src="icon" alt="" class="w-[32px]" /></RouterLink>
        </div>
        <h1 class="hidden lg:block headline-small text-on-surface">{{ pagename }}</h1>
        <nav class="flex gap-2 justify-center items-center min-w-0 w-full lg:w-auto">
            <div class="relative flex items-center lg:hidden">
                <mdui-button-icon icon="menu--outlined" @click="showmenu"></mdui-button-icon>
            </div>
            <div v-if="!store.currentUser?.isAdmin && (perms == null || perms.hasAccessToCartTab || perms.hasAccessToSubmitOrder || perms.hasAccessToOrderItemsTab)" class="relative flex items-center">
                <mdui-badge
                    v-if="basketCount"
                    class="absolute h-auto hidden lg:flex -top-[4px] -right-[4px] z-50 p-[6px] pt-[7px] pe-[5px] text-10 bg-tertiary-container text-on-tertiary-container font-semibold"
                    style="--shape-corner: 10px">
                    {{ basketCount }}
                </mdui-badge>
                <div
                    v-if="basketCount"
                    class="absolute lg:hidden rounded-lg top-[1px] right-[1px] z-50 w-3.5 aspect-square bg-tertiary-container"></div>
                <mdui-button-icon
                    :title="$t('$cart')"
                    :variant="isCart ? 'tonal' : 'text'"
                    icon="shopping_cart--outlined"
                    href="/cart"></mdui-button-icon>
            </div>
            <div class="lg:border-l border-outline-variant ps-2 lg:ps-6 pe-2 lg:pe-4 text-end min-w-0 ms-auto lg:ms-4" v-if="store.currentUser">
                <p class="label-large truncate">{{ store.currentUser.name }}</p>
                <span class="body-small bg-secondary-container text-on-secondary-container py-[2px] px-1" v-if="store.currentDebitor">
                    {{ store.currentDebitor.name }}
                </span>
            </div>
            <div class="flex items-center">
                <mdui-dropdown>
                    <mdui-button-icon variant="filled" :title="$t('general.$menu')" slot="trigger">
                        <span class="label-large">{{ usershort }}</span>
                    </mdui-button-icon>
                    <mdui-menu class="min-w-48"
                               selects="single"
                               :value="store.currentDebitor?.debitorNumber"
                               @change="changeDebitor($event.target.value)">
                        <mdui-menu-item href="/account" class="border-b border-surface-variant"
                                        v-if="!store.currentUser?.isAdmin">
                            {{ $t('nav.$myProfile') }}
                            <mdui-icon slot="icon" name="account_circle--outlined"></mdui-icon>
                        </mdui-menu-item>
                        <mdui-menu-item href="/login/choose-account"
                                        class="bg-surface-container-lowest hidden lg:block"
                                        v-if="store.currentUser?.debitorAccounts.length">
                            {{ $t('nav.$account') }}:
                            <span v-if="store.currentDebitor">{{ store.currentDebitor.name }}</span>
                            <mdui-icon slot="icon" name="switch_account--outlined"></mdui-icon>
                            <mdui-menu-item slot="submenu"
                                            v-for="debitor in store.currentUser?.debitorAccounts"
                                            :key="debitor.debitorNumber"
                                            :value="debitor.debitorNumber"
                                            :class="{ 'menuitem-inactive': store.currentDebitor?.debitorNumber === debitor.debitorNumber }">
                                {{ debitor.name }}
                            </mdui-menu-item>
                        </mdui-menu-item>
                        <mdui-menu-item href="/login/choose-account"
                                        class="bg-surface-container-lowest lg:hidden"
                                        v-if="store.currentUser?.debitorAccounts.length">
                            {{ $t('nav.$changeAccount') }}
                            <mdui-icon slot="icon" name="switch_account--outlined"></mdui-icon>
                        </mdui-menu-item>
                        <mdui-menu-item href="/account/debitors"
                                        v-if="store.currentUser?.debitorAccounts.length && perms?.hasAccessToMyDebitorsTab">
                            {{ $t('nav.$myDebitorAccounts') }}
                            <mdui-icon slot="icon" name="folder_copy--outlined"></mdui-icon>
                        </mdui-menu-item>
                        <!-- Todo: add additional condition to v-if: only for customers that has IsAdmin on any of its debitors -->
                        <mdui-menu-item href="/account/administrate-users"
                                        v-if="!store.currentUser?.isAdmin && (store.currentUser?.debitorAccounts?.some(d => d.debitorPermissions?.isAdmin) || store.currentUser?.debitorAccounts?.some(d => d.debitorPermissions == null))">
                            {{ $t('nav.$administrateUsers') }}
                            <mdui-icon slot="icon" name="groups--outlined"></mdui-icon>
                        </mdui-menu-item>
                        <mdui-menu-item @click="logout" class="border-t border-surface-variant">
                            {{ $t('nav.$logout') }}
                            <mdui-icon slot="icon" name="logout--outlined"></mdui-icon>
                        </mdui-menu-item>
                    </mdui-menu>
                </mdui-dropdown>
            </div>
        </nav>
    </header>
    <h1 class="lg:hidden headline-small text-on-surface p-4 bg-primary/10 border-b border-outline-variant">{{ pagename }}</h1>
</template>

<script setup lang="ts">
    import { computed, onMounted, ref } from 'vue';
    import { useStore } from '@/store';
    import { useRoute, useRouter } from 'vue-router';
    import { useI18n } from 'vue-i18n';
    import { useAuth } from '@/composables/auth';
    import emitter, { Events } from '@/event';
    import BasketService from '@/services/basketService';
    import ImpersonationService from '@/services/impersonationService';
    import InfoService from '@/services/infoService';
    import 'mdui/components/button-icon.js';
    import 'mdui/components/dropdown.js';
    import 'mdui/components/menu.js';
    import 'mdui/components/menu-item.js';
    import 'mdui/components/icon.js';
    import 'mdui/components/badge.js';

    defineProps<{
        isCart: boolean;
    }>();

    // Usings
    const store = useStore();
    const route = useRoute();
    const router = useRouter();
    const { t } = useI18n();
    const { logout } = useAuth();
    const impersonationService = new ImpersonationService();
    const infoService = new InfoService();

    // Data
    const icon = ref('');

    // Extract permissions once, reactive with currentDebitor changes
    const perms = computed(() => store.currentDebitor?.debitorPermissions);

    // Lifecycle
    icon.value = document.querySelector('body')?.getAttribute('data-tertiary-icon') ?? '';

    // Computed
    const pagename = computed(() => {
        return t(`nav.$${String(route.name)}`);
    });

    const usershort = computed(() => {
        if (!store.currentUser || !store.currentUser.name) return;
        let words = store.currentUser.name.split(' ');

        if (words.length == 1) {
            return words[0][0].toUpperCase();
        }

        const firstLast = words[0][0] + words[words.length - 1][0];
        return firstLast.toUpperCase();
    });

    const basketCount = computed(() => {
        return store.basket ? store.basket.lineItems.length : 0;
    });

    // Methods
    function changeDebitor(debitorNumber: string) {
        if (!store.currentUser) return;
        const newDebitor = store.currentUser.debitorAccounts.find((x) => x.debitorNumber == debitorNumber);

        if (newDebitor) {
            // Clear current basket to prevent stale data from showing
            store.basket = undefined;
            store.currentDebitor = newDebitor;
            emitter.emit(Events.UpdateBasket);
            emitter.emit(Events.ChangeDebitor);
        }
    }

    function showmenu() {
        emitter.emit(Events.ShowMenu);
        emitter.emit(Events.ShowRail);
    }
    function showrail() {
        emitter.emit(Events.ShowRail);
    }

</script>
